import React, { useEffect, useRef, useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Mention from '@tiptap/extension-mention';
import ResizableImage from './ResizableImage';
import VideoNode from './VideoNode';
import ImageUploadExtension from './extensions/ImageUpload';
import PasteHandler from './extensions/PasteHandler';
import TipTapToolbar from './TipTapToolbar';
import { buildMentionSuggestion } from './MentionSuggestion';
import { TicketReferenceExtension } from './TicketReferenceExtension';
import './TicketReference.css';

/**
 * Core TipTap editor component.
 * This is the internal implementation — RichTextEditor.js wraps this.
 */
const TipTapEditor = forwardRef(({
  value,
  onChange,
  placeholder = 'Write something...',
  readOnly = false,
  minHeight = 150,
  maxHeight = 400,
  onFocus,
  onBlur,
  className = '',
  // Image upload
  enableImageUpload = false,
  imageUploadEntityType = null,
  imageUploadEntityId = null,
  // Attachments
  enableAttachments = false,
  onAttachFiles,
  // Mentions
  enableMentions = false,
  projectId = null,
  onMention = null,
  // File input refs (passed from wrapper)
  imageInputRef,
  videoInputRef,
  fileInputRef,
}, ref) => {
  const isInternalChange = useRef(false);
  // Tracks the last HTML value sent via onChange so we can tell when the parent
  // has reflected it back as a prop. Used to safely release isInternalChange.
  const pendingValue = useRef(null);
  const editorContainerRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const hasFocusRef = useRef(false);
  const [editorMaxHeight, setEditorMaxHeight] = useState(maxHeight);
  const [isExpanded, setIsExpanded] = useState(false);
  const resizeStartRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(null);

  // Build extensions list
  const extensions = buildExtensions({
    placeholder,
    enableMentions,
    projectId,
    onMention,
    enableImageUpload,
    imageUploadEntityType,
    imageUploadEntityId,
  });

  const editor = useEditor({
    extensions,
    content: value || '',
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: 'tiptap-editor-content',
        style: `min-height: ${minHeight}px;`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      isInternalChange.current = true;
      const html = ed.getHTML();
      const text = ed.getText().trim();
      // If truly empty (no text and no block embeds), return empty string
      const hasContent = text.length > 0 || html.includes('<img') || html.includes('<video') || html.includes('<table') || html.includes('<hr');
      const valueToSend = hasContent ? html : '';
      pendingValue.current = valueToSend;
      onChange(valueToSend);
      // isInternalChange is cleared in the useEffect below once the parent
      // re-renders with value === pendingValue (i.e. it has caught up).
      // Do NOT reset it here via microtask — that creates a window where an
      // unrelated re-render could overwrite the editor with a stale value prop.
    },
  });

  // Sync external value changes into editor
  useEffect(() => {
    if (!editor) return;

    const normalizedValue = value || '';

    // If the parent is echoing back the value we just sent via onChange,
    // this is our own change being reflected — release the guard and skip.
    if (pendingValue.current !== null && normalizedValue === pendingValue.current) {
      isInternalChange.current = false;
      pendingValue.current = null;
      return;
    }

    // An internal change is still in-flight (parent hasn't caught up yet).
    // Don't overwrite the editor with a stale value prop.
    if (isInternalChange.current) return;

    const currentHtml = editor.getHTML();
    // Normalize: treat empty states as equal
    const normalizedCurrent = currentHtml === '<p></p>' ? '' : currentHtml;

    if (normalizedValue !== normalizedCurrent) {
      editor.commands.setContent(normalizedValue || '', false);
    }
  }, [value, editor]);

  // Update editable when readOnly changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  // Wire up upload progress callback to editor storage
  useEffect(() => {
    if (editor?.storage?.imageUpload) {
      editor.storage.imageUpload.onProgress = (state) => setUploadProgress(state);
    }
  }, [editor]);

  // Expose insert methods to parent via ref (scoped to this editor instance)
  useImperativeHandle(ref, () => ({
    insertImage: (url) => {
      if (editor && url) {
        editor.chain().focus().insertContent({
          type: 'resizableImage',
          attrs: { src: url },
        }).run();
      }
    },
    insertVideo: (url) => {
      if (editor && url) {
        editor.chain().focus().insertContent({
          type: 'videoNode',
          attrs: { src: url },
        }).run();
      }
    },
    notifyUploadProgress: (state) => setUploadProgress(state),
    hasActiveUploads: () => (editor?.storage?.imageUpload?.activeUploads || 0) > 0,
  }), [editor]);

  // Container-level focus/blur handling
  // Uses timeout to prevent false blur when clicking toolbar buttons
  const handleContainerBlur = useCallback(() => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);

    blurTimeoutRef.current = setTimeout(() => {
      const container = editorContainerRef.current;
      const activeEl = document.activeElement;

      // Check if focus is still within the editor container
      if (container && container.contains(activeEl)) {
        return;
      }

      // Check for mention suggestion dropdown or color picker
      if (activeEl && (
        activeEl.closest('.mention-suggestion-portal') ||
        activeEl.closest('.tiptap-toolbar') ||
        activeEl.closest('.tiptap-editor-wrapper') ||
        activeEl.closest('.tiptap-color-picker')
      )) {
        return;
      }

      if (hasFocusRef.current) {
        hasFocusRef.current = false;
        if (onBlur) onBlur();
      }
    }, 150);
  }, [onBlur]);

  const handleContainerFocus = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    if (!hasFocusRef.current) {
      hasFocusRef.current = true;
      if (onFocus) onFocus();
    }
  }, [onFocus]);

  // Resize handle drag
  const handleResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeStartRef.current = { y: e.clientY, height: editorMaxHeight };

    const handleMouseMove = (moveEvent) => {
      const diff = moveEvent.clientY - resizeStartRef.current.y;
      const newHeight = Math.max(minHeight, resizeStartRef.current.height + diff);
      setEditorMaxHeight(newHeight);
      setIsExpanded(false);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      resizeStartRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [editorMaxHeight, minHeight]);

  const toggleExpand = useCallback(() => {
    if (isExpanded) {
      setEditorMaxHeight(maxHeight);
      setIsExpanded(false);
    } else {
      setEditorMaxHeight(9999);
      setIsExpanded(true);
    }
  }, [isExpanded, maxHeight]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  if (!editor) return null;

  return (
    <div
      ref={editorContainerRef}
      className={`tiptap-editor-wrapper ${readOnly ? 'readonly' : ''} ${className}`}
      onBlur={handleContainerBlur}
      onFocus={handleContainerFocus}
    >
      {!readOnly && (
        <TipTapToolbar
          editor={editor}
          enableImageUpload={enableImageUpload}
          enableAttachments={enableAttachments}
          imageInputRef={imageInputRef}
          videoInputRef={videoInputRef}
          fileInputRef={fileInputRef}
        />
      )}
      <div
        className="tiptap-editor-container"
        style={{ maxHeight: isExpanded ? 'none' : `${editorMaxHeight}px` }}
      >
        <EditorContent editor={editor} />
      </div>
      {uploadProgress && (
        <div className={`tiptap-upload-progress ${uploadProgress.status}`}>
          <div className="tiptap-upload-progress-info">
            <span className="tiptap-upload-progress-icon">
              {uploadProgress.type === 'video' ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
                </svg>
              )}
            </span>
            <span className="tiptap-upload-progress-text">
              {uploadProgress.status === 'error'
                ? `Upload failed: ${uploadProgress.fileName}`
                : `Uploading ${uploadProgress.fileName}`}
            </span>
            <span className="tiptap-upload-progress-percent">
              {uploadProgress.status === 'error' ? '' : `${uploadProgress.percent}%`}
            </span>
          </div>
          <div className="tiptap-upload-progress-bar-track">
            <div
              className="tiptap-upload-progress-bar-fill"
              style={{ width: `${uploadProgress.percent}%` }}
            />
          </div>
        </div>
      )}
      {!readOnly && (
        <div
          className="tiptap-resize-bar"
          onMouseDown={(e) => {
            // Prevent editor blur when interacting with resize bar
            // This keeps focus in the ProseMirror editor so the parent
            // blur handler (e.g. description auto-close) doesn't fire
            e.preventDefault();
          }}
        >
          <div
            className="tiptap-resize-handle"
            onMouseDown={handleResizeMouseDown}
            title="Drag to resize"
          >
            <svg width="20" height="6" viewBox="0 0 20 6" fill="currentColor">
              <circle cx="4" cy="1" r="1" /><circle cx="10" cy="1" r="1" /><circle cx="16" cy="1" r="1" />
              <circle cx="4" cy="5" r="1" /><circle cx="10" cy="5" r="1" /><circle cx="16" cy="5" r="1" />
            </svg>
          </div>
          <button
            type="button"
            className="tiptap-expand-btn"
            onClick={toggleExpand}
            title={isExpanded ? 'Collapse editor' : 'Expand editor'}
          >
            {isExpanded ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" />
                <line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            )}
          </button>
        </div>
      )}
    </div>
  );
});

TipTapEditor.displayName = 'TipTapEditor';

/**
 * Build the extensions array based on features enabled
 */
function buildExtensions({
  placeholder,
  enableMentions,
  projectId,
  onMention,
  enableImageUpload,
  imageUploadEntityType,
  imageUploadEntityId,
}) {
  const exts = [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      codeBlock: {
        HTMLAttributes: { class: 'code-block' },
      },
      // Disable default image since we use ResizableImage
      // StarterKit doesn't include image, so nothing to disable
    }),
    Underline,
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        rel: 'noopener noreferrer',
        target: '_blank',
      },
    }),
    TextStyle,
    Color,
    Highlight.configure({ multicolor: true }),
    Placeholder.configure({
      placeholder,
      emptyEditorClass: 'is-editor-empty',
    }),
    Table.configure({
      resizable: false,
      HTMLAttributes: { class: 'editor-table' },
    }),
    TableRow,
    TableCell,
    TableHeader,
    ResizableImage,
    VideoNode,
    PasteHandler,
  ];

  if (enableMentions && projectId) {
    // Extend the Mention node to output backward-compatible HTML
    const CustomMention = Mention.extend({
      renderHTML({ node, HTMLAttributes }) {
        return [
          'strong',
          {
            style: 'color: #0052cc;',
            'data-mention-id': node.attrs.id || '',
            class: 'mention-tag',
          },
          `@${node.attrs.label || ''}`,
        ];
      },
      parseHTML() {
        return [
          { tag: 'strong[data-mention-id]' },
          { tag: 'span[data-type="mention"]' },
        ];
      },
    });

    exts.push(
      CustomMention.configure({
        HTMLAttributes: {
          class: 'mention-tag',
        },
        suggestion: buildMentionSuggestion(projectId, onMention),
      })
    );
  }

  // Ticket key auto-linking: type "RGDP-228 " → inline chip with title + status
  exts.push(TicketReferenceExtension);

  if (enableImageUpload && imageUploadEntityType && imageUploadEntityId) {
    exts.push(
      ImageUploadExtension.configure({
        entityType: imageUploadEntityType,
        entityId: imageUploadEntityId,
      })
    );
  }

  return exts;
}

export default TipTapEditor;
